import { jobLists } from "character";
import BasicIcon from "components/basic-icon/BasicIcon";
import FormComponents from "components/forms/FormComponents";
import JobIcon from "components/job-icon/JobIcon";
import Link from "components/link/Link";
import NoCharacter from "components/no-character/NoCharacter";
import RelicCheckbox from "components/relic-checkbox/RelicCheckbox";
import Tip from "components/tip/Tip";
import { dungeons, trials } from "data-files/duties";
import { enemies } from "data-files/enemies";
import { items } from "data-files/items";
import { locations } from "data-files/locations";
import { quests } from "data-files/quests";
import { EorzeaDataType, GameData, Icon, InputType, LinkType, RelicData, View } from "enums";
import manager from "managers/app";
import { ViewRelicInfo } from "managers/types/relic-types";
import { Component } from "react";
import { connect } from "react-redux";
import t from "translations/translator";
import { AppState } from "types/app";
import utils from "util/utils";
import ContentHeader from "views/page-parts/content/content-header/ContentHeader";
import DetailsSection from "views/page-parts/content/details-section/DetailsSection";
import Obtained from "views/page-parts/content/obtained/Obtained";
import OverviewTable from "views/page-parts/content/overview-table/OverviewTable";
import { Requirement } from "views/page-parts/content/relic-requirements/types";
import { ViewWeaponZodiacBaseWeaponProps } from "views/relics/weapons/zodiac/base-weapon/types";
import { TaskInfo } from "views/type";
import "./WeaponZodiacBaseWeapon.css";

const mapStateToProps = (state:AppState) => {
	return {
		lang: state.userInfo.options.lang,
		gameLang: state.userInfo.options.gameLang,
		userInfo: state.userInfo,
		hideCompletedSteps: state.userInfo.options.hideCompletedSteps,
		hideCompletedTasks: state.userInfo.options.hideCompletedTasks,
		showJob: state.userInfo.options.job,
	};
};

class WeaponZodiacBaseWeapon extends Component<ViewWeaponZodiacBaseWeaponProps> {
	private requirements:Requirement[] = [];
	private importantNPCs:GameData.NPC[] = [];
	private relicInfo:ViewRelicInfo = {
		jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC],
		type: RelicData.Type.WEAPON,
		relic: RelicData.Group.WEAPON_ZODIAC,
		part: RelicData.Part.WEAPON_BASE_WEAPON,
		steps: [RelicData.Step.BROKEN_WEAPON, RelicData.Step.CLASS_WEAPON, RelicData.Step.CHIMERA, RelicData.Step.AMDAPOR, RelicData.Step.BEASTMAN, RelicData.Step.HYDRA, RelicData.Step.EMBER, RelicData.Step.GALE, RelicData.Step.ORE, RelicData.Step.OIL],
		stepPlaceholders: {
			[RelicData.Step.CHIMERA]: { TRIAL: GameData.Trial.CHIMERA },
			[RelicData.Step.AMDAPOR]: { DUNGEON: GameData.Dungeon.AMDAPOR_KEEP },
			[RelicData.Step.HYDRA]: { TRIAL: GameData.Trial.HYDRA },
			[RelicData.Step.EMBER]: { ITEM: "White-Hot Ember" },
			[RelicData.Step.GALE]: { ITEM: "Howling Gale" },
			[RelicData.Step.ORE]: { ITEM: "Hyperfused Ore" },
			[RelicData.Step.OIL]: { ITEM: GameData.Item.QUENCHING_OIL },
		},
		next: {
			part: RelicData.Part.WEAPON_ZENITH,
			view: View.RELIC_WEAPONS_ZODIAC_ZENITH,
		},
		previous: null,
	};

	constructor(props:ViewWeaponZodiacBaseWeaponProps){
		super(props);
		this.requirements = this.getRequirements();
		this.importantNPCs = this.getImportantNPCs();
	}

	
	render():JSX.Element{
		const character = manager.data.getActiveCharacter();
		if(character === null){ return <NoCharacter />; }

		const completeSteps = manager.content.getCompleteSteps(character.progress, this.relicInfo);

		let row = "odd-row";
		const oilStepInventory = manager.data.getInventoryStatus({
			item: manager.relics.getTaskItem(RelicData.Step.OIL, RelicData.Task.TASK_1),
			relic: [this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.OIL, RelicData.Task.TASK_1],
		});

		return (
			<div id="weapon-zodiac-base-weapon" className="relic">
				<ContentHeader requirements={this.requirements} importantNPCs={this.importantNPCs} relicInfo={this.relicInfo}>
					<p>
						{t.t("In order to obtain the %PART% you must complete the quest", { PART: RelicData.Part.WEAPON_BASE_WEAPON })}
						<Link type={LinkType.LODESTONE} lodestoneType={EorzeaDataType.BLUEQUEST} text={GameData.Quest.RELIC_REBORN} lodestoneID={quests[GameData.Quest.RELIC_REBORN].lodestoneID} />
					</p>
					<p>{t.t("The quest follows the steps below")}</p>
				</ContentHeader>
				<div id="relic-checklist">
					<OverviewTable character={character} completeSteps={completeSteps} relicInfo={this.relicInfo} />
				</div>
				<div id="relic-details">
					<h1>{t.t("Details")}</h1>
					{this.props.hideCompletedSteps && completeSteps.allComplete ? t.t("All Steps Complete") : null }
					<DetailsSection title={RelicData.Step.BROKEN_WEAPON} id={RelicData.Step.BROKEN_WEAPON} stepComplete={completeSteps[RelicData.Step.BROKEN_WEAPON]}><p>{t.t("The Broken Weapon is obtained during the first parts of the quest")}</p></DetailsSection>
					<DetailsSection title={RelicData.Step.CLASS_WEAPON} id={RelicData.Step.CLASS_WEAPON} stepComplete={completeSteps[RelicData.Step.CLASS_WEAPON]}>
						<p>{t.t("Each job requires a specific weapon with 2 Grade III Materia melded")}</p>
						<div className="table-wrapper">
							<table className="table">
								<thead><tr><th>{t.t("Job")}</th><th>{t.t("Task")}</th><th>{t.t("Found")}</th><th>{t.t("Quantity")}</th><th>{t.t("Inventory")}</th><th>{t.t("To Obtain")}</th><th>{t.t("Done")}</th></tr></thead>
								<tbody>
									{
										this.relicInfo.jobs.map((job) => {
											if(this.props.showJob !== null && this.props.showJob !== job){ return null; }

											const stepComplete = character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.CLASS_WEAPON, job);
											if(this.props.hideCompletedSteps && stepComplete){ return null; }

											const taskInfo:TaskInfo[] = [
												{ task: RelicData.Task.TASK_1, complete: character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.CLASS_WEAPON, RelicData.Task.TASK_1, job), target: manager.relics.getTaskItem(RelicData.Step.CLASS_WEAPON, RelicData.Task.TASK_1, job) },
												{ task: RelicData.Task.TASK_2, complete: character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.CLASS_WEAPON, RelicData.Task.TASK_2, job), target: manager.relics.getTaskItem(RelicData.Step.CLASS_WEAPON, RelicData.Task.TASK_2, job) },
											];

											const tableRows = taskInfo.map((thisTask, taskIndex) => {
												if(this.props.hideCompletedTasks && thisTask.complete){ return null; }

												let thisTarget = GameData.Item.UNKNOWN;
												if(utils.guards.isEnumValue(GameData.Item, thisTask.target)){ thisTarget = thisTask.target; }

												const inventory = manager.data.getInventoryStatus({
													item: thisTarget,
													relic: [this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.CLASS_WEAPON, thisTask.task],
													job: job,
												});

												const showJobIcon = manager.content.shouldShowJobIcon(this.props.hideCompletedTasks, taskInfo, taskIndex);
												const rowSpan = manager.content.getRowSpan(this.props.hideCompletedTasks, taskInfo, taskIndex);

												return <tr key={`${job}-${RelicData.Step.CLASS_WEAPON}-${thisTask.task}`} className={row}>
													{showJobIcon ? <td rowSpan={rowSpan}><JobIcon job={job} /></td> : null}
													<td>{t.t(thisTarget)}</td>
													<td><Obtained obtained={items[thisTarget].obtained} qtyNeeded={inventory.total} /></td>
													<td>{inventory.qtyPerJob.toLocaleString()}</td>
													<td><FormComponents.TextInput label={{ hide: true }} input={{ type: InputType.NUMBER, value: inventory.inventory, dataAttributes: { item: thisTarget }, events: { onChange: manager.content.inventoryUpdated }}} /></td>
													<td>{inventory.toObtain.toLocaleString()}</td>
													<td><RelicCheckbox character={character} job={job} relicInfo={this.relicInfo} step={RelicData.Step.CLASS_WEAPON} task={thisTask.task} /></td>
												</tr>;
											});
											row = row === "odd-row" ? "even-row" : "odd-row";
											return tableRows;
										})
									}
									{
										completeSteps[RelicData.Step.CLASS_WEAPON]
											? <tr className="all-complete"><td colSpan={7}><p>{t.t("All Complete")}</p></td></tr>
											: null
									}
								</tbody>
							</table>
						</div>
					</DetailsSection>
					<DetailsSection title={RelicData.Step.CHIMERA} placeholders={this.relicInfo.stepPlaceholders[RelicData.Step.CHIMERA]} id={RelicData.Step.CHIMERA} stepComplete={completeSteps[RelicData.Step.CHIMERA]}><p>{t.t("Complete")}<Link type={LinkType.LODESTONE} lodestoneType={trials[GameData.Trial.CHIMERA].type} text={trials[GameData.Trial.CHIMERA].name} lodestoneID={trials[GameData.Trial.CHIMERA].lodestoneID} /></p></DetailsSection>
					<DetailsSection title={RelicData.Step.AMDAPOR} placeholders={this.relicInfo.stepPlaceholders[RelicData.Step.AMDAPOR]} id={RelicData.Step.AMDAPOR} stepComplete={completeSteps[RelicData.Step.AMDAPOR]}>
						<Tip text={"While in Mor Dhona for this step, Purchase 1x %OIL% & 3x %MIST% for each job, you will need these later"} placeholders={{ OIL: GameData.Item.QUENCHING_OIL, MIST: GameData.Item.THAVNAIRIAN_MIST }}/>
						<p>{t.t("Complete")}<Link type={LinkType.LODESTONE} lodestoneType={dungeons[GameData.Dungeon.AMDAPOR_KEEP].type} text={dungeons[GameData.Dungeon.AMDAPOR_KEEP].name} lodestoneID={dungeons[GameData.Dungeon.AMDAPOR_KEEP].lodestoneID} /></p>
					</DetailsSection>
					<DetailsSection title={RelicData.Step.BEASTMAN} id={RelicData.Step.BEASTMAN} stepComplete={completeSteps[RelicData.Step.BEASTMAN]}>
						<p>{t.t("Each job has a specific set of enemies to hunt")}</p>
						<div className="table-wrapper">
							<table className="table">
								<thead><tr><th>{t.t("Job")}</th><th>{t.t("Target")}</th><th>{t.t("Location")}</th><th>{t.t("Quantity")}</th><th>{t.t("Done")}</th></tr></thead>
								<tbody>
									{
										this.relicInfo.jobs.map((job) => {
											if(this.props.showJob !== null && this.props.showJob !== job){ return null; }

											const stepComplete = character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.BEASTMAN, job);
											if(this.props.hideCompletedSteps && stepComplete){ return null; }

											const taskInfo:TaskInfo[] = [
												{ task: RelicData.Task.TASK_1, complete: character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.BEASTMAN, RelicData.Task.TASK_1, job), target: manager.relics.getTaskEnemy(RelicData.Step.BEASTMAN, RelicData.Task.TASK_1, job) },
												{ task: RelicData.Task.TASK_2, complete: character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.BEASTMAN, RelicData.Task.TASK_2, job), target: manager.relics.getTaskEnemy(RelicData.Step.BEASTMAN, RelicData.Task.TASK_2, job) },
												{ task: RelicData.Task.TASK_3, complete: character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.BEASTMAN, RelicData.Task.TASK_3, job), target: manager.relics.getTaskEnemy(RelicData.Step.BEASTMAN, RelicData.Task.TASK_3, job) },
											];

											const tableRows = taskInfo.map((thisTask, taskIndex) => {
												if(this.props.hideCompletedTasks && thisTask.complete){ return null; }

												let thisTarget = GameData.Enemy.UNKNOWN;
												if(utils.guards.isEnumValue(GameData.Enemy, thisTask.target)){ thisTarget = thisTask.target; }

												const showJobIcon = manager.content.shouldShowJobIcon(this.props.hideCompletedTasks, taskInfo, taskIndex);
												const rowSpan = manager.content.getRowSpan(this.props.hideCompletedTasks, taskInfo, taskIndex);

												const obtainedLocation = enemies[thisTarget].found[0];
												const thisLocation = locations[obtainedLocation.location];

												return <tr key={`${job}-${RelicData.Step.BEASTMAN}-${thisTask.task}`} className={row}>
													{showJobIcon ? <td rowSpan={rowSpan}><JobIcon job={job} /></td> : null}
													<td>{t.t(thisTask.target)}</td>
													{showJobIcon
														? <td rowSpan={rowSpan}>
															<div className="location-wrapper">
																<div className="location">
																	<span><BasicIcon icon={Icon.MAP} tooltip="Map" /> {t.t(thisLocation.map)}</span>
																	{thisLocation.region ? <span><BasicIcon icon={Icon.REGION} tooltip="Region" /> {t.t(thisLocation.region)}</span> : null}
																	{thisLocation.teleport ? <span><BasicIcon icon={Icon.TELEPORT} tooltip="Teleport" /> {t.t(thisLocation.teleport)}</span> : null}
																	{obtainedLocation.x > -1 ? <span><BasicIcon icon={Icon.LOCATION} tooltip="Co-ordinates" /> X: {obtainedLocation.x}, Y: {obtainedLocation.y}</span> : null}
																</div>
															</div>
														</td>
														: null
													}
													<td>8</td>
													<td><RelicCheckbox character={character} job={job} relicInfo={this.relicInfo} step={RelicData.Step.BEASTMAN} task={thisTask.task} /></td>
												</tr>;
											});
											row = row === "odd-row" ? "even-row" : "odd-row";
											return tableRows;
										})
									}
									{
										character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.CLASS_WEAPON)
											? <tr className="all-complete"><td colSpan={5}><p>{t.t("All Complete")}</p></td></tr>
											: null
									}
								</tbody>
							</table>
						</div>
					</DetailsSection>
					<DetailsSection title={RelicData.Step.HYDRA} placeholders={this.relicInfo.stepPlaceholders[RelicData.Step.HYDRA]} id={RelicData.Step.HYDRA} stepComplete={completeSteps[RelicData.Step.HYDRA]}><p>{t.t("Complete")}<Link type={LinkType.LODESTONE} lodestoneType={trials[GameData.Trial.HYDRA].type} text={trials[GameData.Trial.HYDRA].name} lodestoneID={trials[GameData.Trial.HYDRA].lodestoneID} /></p></DetailsSection>
					<DetailsSection title={RelicData.Step.EMBER} placeholders={this.relicInfo.stepPlaceholders[RelicData.Step.EMBER]} id={RelicData.Step.EMBER} stepComplete={completeSteps[RelicData.Step.EMBER]}><p>{t.t("Complete")}<Link type={LinkType.LODESTONE} lodestoneType={trials[GameData.Trial.IFRIT_HARD].type} text={trials[GameData.Trial.IFRIT_HARD].name} lodestoneID={trials[GameData.Trial.IFRIT_HARD].lodestoneID} /></p></DetailsSection>
					<DetailsSection title={RelicData.Step.GALE} placeholders={this.relicInfo.stepPlaceholders[RelicData.Step.GALE]} id={RelicData.Step.GALE} stepComplete={completeSteps[RelicData.Step.GALE]}><p>{t.t("Complete")}<Link type={LinkType.LODESTONE} lodestoneType={trials[GameData.Trial.GARUDA_HARD].type} text={trials[GameData.Trial.GARUDA_HARD].name} lodestoneID={trials[GameData.Trial.GARUDA_HARD].lodestoneID} /></p></DetailsSection>
					<DetailsSection title={RelicData.Step.ORE} placeholders={this.relicInfo.stepPlaceholders[RelicData.Step.ORE]} id={RelicData.Step.ORE} stepComplete={completeSteps[RelicData.Step.ORE]}><p>{t.t("Complete")}<Link type={LinkType.LODESTONE} lodestoneType={trials[GameData.Trial.TITAN_HARD].type} text={trials[GameData.Trial.TITAN_HARD].name} lodestoneID={trials[GameData.Trial.TITAN_HARD].lodestoneID} /></p></DetailsSection>
					<DetailsSection title={RelicData.Step.OIL} placeholders={this.relicInfo.stepPlaceholders[RelicData.Step.OIL]} id={RelicData.Step.OIL} stepComplete={completeSteps[RelicData.Step.OIL]}>
						<div className="table-wrapper">
							<table className="table">
								<thead><tr><th>{t.t("Obtained")}</th><th>{t.t("Item")}</th><th>{t.t("Quantity / Job")}</th><th>{t.t("Total")}</th><th>{t.t("Used")}</th><th>{t.t("Remaining")}</th><th>{t.t("Inventory")}</th><th>{t.t("To Obtain")}</th></tr></thead>
								<tbody>
									{this.props.hideCompletedTasks && oilStepInventory.toObtain === 0
										? null
										: <tr className="odd-row">
											<td><Obtained obtained={items[GameData.Item.QUENCHING_OIL].obtained} qtyNeeded={oilStepInventory.total} /></td>
											<td>{t.t(items[GameData.Item.QUENCHING_OIL].name)}</td>
											<td>{oilStepInventory.qtyPerJob.toLocaleString()}</td>
											<td>{oilStepInventory.total.toLocaleString()}</td>
											<td>{oilStepInventory.used.toLocaleString()}</td>
											<td>{oilStepInventory.remaining.toLocaleString()}</td>
											<td><FormComponents.TextInput label={{ hide: true }} input={{ type: InputType.NUMBER, value: oilStepInventory.inventory, dataAttributes: { item: GameData.Item.QUENCHING_OIL }, events: { onChange: manager.content.inventoryUpdated }}} /></td>
											<td>{oilStepInventory.toObtain.toLocaleString()}</td>
										</tr>
									}
									{
										character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.OIL)
											? <tr className="all-complete"><td colSpan={8}><p>{t.t("All Complete")}</p></td></tr>
											: null
									}
								</tbody>
							</table>
						</div>
					</DetailsSection>
				</div>
			</div>
		);
	}

	componentDidMount(){
		manager.view.changeComplete();
	}

	private getRequirements():Requirement[]{
		return [
			{ type: quests[GameData.Quest.ULTIMATE_WEAPON].type, text: GameData.Quest.ULTIMATE_WEAPON, lodestoneID: quests[GameData.Quest.ULTIMATE_WEAPON].lodestoneID },
			{ type: "text", text: "Complete all Job Quests (level 50) for your chosen jobs" },
			{ type: quests[GameData.Quest.WEAPONSMITH_OF_LEGEND].type, text: GameData.Quest.WEAPONSMITH_OF_LEGEND, lodestoneID: quests[GameData.Quest.WEAPONSMITH_OF_LEGEND].lodestoneID },
		];
	}

	private getImportantNPCs():GameData.NPC[]{
		return [GameData.NPC.NEDRICK, GameData.NPC.GEROLT_NORTH_SHROUD];
	}
}
export default connect(mapStateToProps)(WeaponZodiacBaseWeapon);
